import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faFacebook } from '@fortawesome/free-brands-svg-icons';
import emailjs from 'emailjs-com';

const ContactUs = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'שם מלא הוא שדה חובה';
    }
    
    if (!formData.email && !formData.phone) {
      errors.email = 'יש להזין אימייל או טלפון';
      errors.phone = 'יש להזין אימייל או טלפון';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'יש להזין כתובת אימייל תקינה';
    }
    
    if (formData.phone && !/^0\d{8,9}$/.test(formData.phone)) {
      errors.phone = 'יש להזין מספר טלפון תקין';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: undefined
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSending(true);
    setFormError(false);

    emailjs
      .send(
        'service_57fj4n9', 
        'template_i1vru3k', 
        formData,
        'wFi9VAP5cqkrjZKFw'
      )
      .then(
        () => {
          setFormSubmitted(true);
          setFormData({ name: '', email: '', phone: '', message: '' });
          setIsSending(false);
        },
        (error) => {
          console.error('Error:', error);
          setFormError(true);
          setIsSending(false);
        }
      );
  };

  const resetForm = () => {
    setFormSubmitted(false);
    setFormError(false);
    setFieldErrors({});
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <PageContainer>
      <ContactContainer>
        <HeaderContainer>
          <IconDecoration />
          <Title>יצירת קשר</Title>
          <Subtitle>נשמח לענות לכל שאלה או בקשה. אנחנו כאן בשבילכם!</Subtitle>
        </HeaderContainer>

        {/* Contact Icons Section */}
        <IconsContainer>
          <ContactOption>
            <IconLink href="tel:+972508857282" color="#4a6da7">
              <IconCircle bgColor="#4a6da7">
                <FontAwesomeIcon icon={faPhone} />
              </IconCircle>
              <IconLabel>התקשרו אלינו</IconLabel>
            </IconLink>
          </ContactOption>
          
          <ContactOption>
            <IconLink href="mailto:mashkanta.mishtalemet@gmail.com" color="#DB4437">
              <IconCircle bgColor="#DB4437">
                <FontAwesomeIcon icon={faEnvelope} />
              </IconCircle>
              <IconLabel>שלחו לנו מייל</IconLabel>
            </IconLink>
          </ContactOption>
          
          <ContactOption>
            <IconLink href="https://wa.me/972508857282" target="_blank" rel="noopener noreferrer" color="#25D366">
              <IconCircle bgColor="#25D366">
                <FontAwesomeIcon icon={faWhatsapp} />
              </IconCircle>
              <IconLabel>וואטסאפ</IconLabel>
            </IconLink>
          </ContactOption>
          
          <ContactOption>
            <IconLink href="https://www.facebook.com/mashkanta.experience" target="_blank" rel="noopener noreferrer" color="#4267B2">
              <IconCircle bgColor="#4267B2">
                <FontAwesomeIcon icon={faFacebook} />
              </IconCircle>
              <IconLabel>פייסבוק</IconLabel>
            </IconLink>
          </ContactOption>
        </IconsContainer>

        {/* Contact Form Section */}
        <FormContainer>
          {formSubmitted ? (
            <SuccessContainer>
              <SuccessIcon>
                <FontAwesomeIcon icon={faCheckCircle} />
              </SuccessIcon>
              <SuccessTitle>תודה שיצרתם קשר!</SuccessTitle>
              <SuccessMessage>הפנייה שלך התקבלה בהצלחה. נחזור אליך בהקדם האפשרי.</SuccessMessage>
              <BackButton onClick={resetForm}>חזרה לטופס יצירת קשר</BackButton>
            </SuccessContainer>
          ) : (
            <>
              <FormHeader>
                <FormTitle>השאירו פרטים ונחזור אליכם</FormTitle>
                <FormSubtitle>אנו מתחייבים לזמן תגובה מהיר ויחס אישי</FormSubtitle>
              </FormHeader>
              
              {formError && (
                <ErrorMessage>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>אירעה שגיאה בשליחת הטופס. אנא נסו שוב או צרו קשר באחת הדרכים האחרות.</span>
                </ErrorMessage>
              )}
              
              <ContactForm onSubmit={handleSubmit} noValidate>
                <FormLabel hasError={!!fieldErrors.name}>
                  <FormLabelText>שם מלא<Required>*</Required></FormLabelText>
                  <FormInput
                    name="name"
                    type="text"
                    placeholder="הכנס את שמך המלא"
                    value={formData.name}
                    onChange={handleChange}
                    hasError={!!fieldErrors.name}
                    required
                  />
                  {fieldErrors.name && <ErrorText>{fieldErrors.name}</ErrorText>}
                </FormLabel>
                
                <TwoColumns>
                  <FormLabel hasError={!!fieldErrors.phone}>
                    <FormLabelText>טלפון</FormLabelText>
                    <FormInput
                      name="phone"
                      type="tel"
                      placeholder="הכנס את מספר הטלפון שלך"
                      value={formData.phone}
                      onChange={handleChange}
                      hasError={!!fieldErrors.phone}
                    />
                    {fieldErrors.phone && <ErrorText>{fieldErrors.phone}</ErrorText>}
                  </FormLabel>
                  
                  <FormLabel hasError={!!fieldErrors.email}>
                    <FormLabelText>אימייל</FormLabelText>
                    <FormInput
                      name="email"
                      type="email"
                      placeholder="הכנס את כתובת האימייל שלך"
                      value={formData.email}
                      onChange={handleChange}
                      hasError={!!fieldErrors.email}
                    />
                    {fieldErrors.email && <ErrorText>{fieldErrors.email}</ErrorText>}
                  </FormLabel>
                </TwoColumns>
                
                <FormLabel>
                  <FormLabelText>הודעה</FormLabelText>
                  <FormTextarea
                    name="message"
                    placeholder="אנא כתוב את הודעתך כאן (אופציונלי)"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                  />
                </FormLabel>
                
                <SubmitButton type="submit" disabled={isSending}>
                  {isSending ? (
                    <>
                      <LoadingSpinner />
                      <span>שולח...</span>
                    </>
                  ) : (
                    'שלח הודעה'
                  )}
                </SubmitButton>
              </ContactForm>
            </>
          )}
        </FormContainer>
      </ContactContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  background: linear-gradient(to bottom, #f7f9fc, #eef1f5);
  padding: 40px 0;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  font-family: "Alef", sans-serif;
  direction: rtl;
  
  @media (max-width: 576px) {
    padding: 20px 0;
  }
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 900px;
  width: 95%;
  margin: auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  position: relative;
  
  @media (max-width: 768px) {
    width: 90%;
  }
  
  @media (max-width: 576px) {
    width: 95%;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const HeaderContainer = styled.div`
  text-align: center;
  padding: 40px 20px 30px;
  position: relative;
  
  @media (max-width: 576px) {
    padding: 35px 15px 25px;
  }
`;

const IconDecoration = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #dfe9f3 0%, #fff 100%);
  position: absolute;
  top: -50px;
  right: -50px;
  border-radius: 50%;
  z-index: 0;
  
  &::before {
    content: "";
    position: absolute;
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, #dfeaff 0%, #f0f4f8 100%);
    border-radius: 50%;
    top: 60px;
    right: 90px;
  }
  
  &::after {
    content: "";
    position: absolute;
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #e2f0fb 0%, #f7f9fc 100%);
    border-radius: 50%;
    top: 100px;
    right: 30px;
  }
  
  @media (max-width: 576px) {
    width: 80px;
    height: 80px;
    top: -40px;
    right: -30px;
    
    &::before {
      width: 60px;
      height: 60px;
      top: 50px;
      right: 70px;
    }
    
    &::after {
      width: 40px;
      height: 40px;
      top: 80px;
      right: 25px;
    }
  }
`;

const Title = styled.h1`
  font-size: 2.8rem;
  color: #192a56;
  margin-bottom: 15px;
  position: relative;
  z-index: 1;
  
  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    right: calc(50% - 40px);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #0076d6, #00a7d6);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
  
  @media (max-width: 576px) {
    font-size: 2rem;
    margin-bottom: 12px;
    
    &::after {
      bottom: -8px;
      width: 60px;
      right: calc(50% - 30px);
      height: 3px;
    }
  }
`;

const Subtitle = styled.p`
  color: #596275;
  font-size: 1.1rem;
  margin-top: 20px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.95rem;
    margin-top: 15px;
    padding: 0 5px;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin: 10px auto 40px;
  padding: 0 20px;
  max-width: 800px;
  
  @media (max-width: 768px) {
    gap: 15px;
    margin-bottom: 30px;
  }
  
  @media (max-width: 576px) {
    gap: 10px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    padding: 0 15px;
    margin-bottom: 25px;
  }
`;

const ContactOption = styled.div`
  flex: 1;
  min-width: 150px;
  max-width: 200px;
  text-align: center;
  
  @media (max-width: 768px) {
    min-width: 120px;
  }
  
  @media (max-width: 576px) {
    min-width: unset;
    max-width: unset;
    width: 100%;
  }
`;

const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${props => props.bgColor};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  transition: all 0.3s ease;
  font-size: 1.8rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 576px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
`;

const IconLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ color }) => color};
  transition: all 0.3s ease;
  text-decoration: none;
  padding: 15px;
  border-radius: 12px;
  background-color: rgba(240, 244, 248, 0.7);
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    background-color: white;
    
    ${IconCircle} {
      transform: scale(1.1);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 576px) {
    padding: 12px 8px;
    border-radius: 10px;
  }
`;

const IconLabel = styled.span`
  font-size: 0.9rem;
  margin-top: 10px;
  color: #555;
  font-weight: 500;
  
  @media (max-width: 576px) {
    font-size: 0.85rem;
    margin-top: 8px;
  }
`;

const FormContainer = styled.div`
  background: linear-gradient(to bottom, #ffffff, #f9fafc);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -5px 20px rgba(0, 0, 0, 0.03);
  padding: 40px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
  
  @media (max-width: 576px) {
    padding: 25px 15px;
    border-radius: 20px 20px 0 0;
  }
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  @media (max-width: 576px) {
    margin-bottom: 25px;
  }
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  color: #192a56;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
`;

const FormSubtitle = styled.p`
  color: #596275;
  font-size: 1rem;
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
  
  @media (max-width: 576px) {
    gap: 20px;
  }
`;

const TwoColumns = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  position: relative;
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    right: 0;
    height: 2px;
    width: 100%;
    background: ${props => props.hasError ? '#ff6b6b' : '#e0e0e0'};
    transform: scaleX(${props => props.hasError ? '1' : '0'});
    transform-origin: right;
    transition: transform 0.3s ease;
  }
  
  &:focus-within::after {
    transform: scaleX(1);
    background: ${props => props.hasError ? '#ff6b6b' : '#0076d6'};
  }
  
  @media (max-width: 576px) {
    gap: 6px;
  }
`;

const FormLabelText = styled.span`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  display: flex;
  align-items: center;
  
  @media (max-width: 576px) {
    font-size: 0.95rem;
  }
`;

const Required = styled.span`
  color: #ff6b6b;
  margin-right: 4px;
`;

const FormInput = styled.input`
  padding: 14px;
  border: 1px solid ${props => props.hasError ? '#ff6b6b' : '#dddfe2'};
  border-radius: 8px;
  font-size: 1rem;
  color: #333;
  transition: all 0.3s ease;
  background-color: ${props => props.hasError ? 'rgba(255, 107, 107, 0.05)' : 'white'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#0076d6'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(255, 107, 107, 0.2)' : 'rgba(0, 118, 214, 0.1)'};
  }
  
  &::placeholder {
    color: #a0a0a0;
  }
  
  @media (max-width: 576px) {
    padding: 15px 12px;
    font-size: 16px; /* Prevent zoom on focus in iOS */
    border-radius: 10px;
  }
`;

const FormTextarea = styled.textarea`
  padding: 14px;
  border: 1px solid #dddfe2;
  border-radius: 8px;
  font-size: 1rem;
  color: #333;
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 120px;
  
  &:focus {
    outline: none;
    border-color: #0076d6;
    box-shadow: 0 0 0 3px rgba(0, 118, 214, 0.1);
  }
  
  &::placeholder {
    color: #a0a0a0;
  }
  
  @media (max-width: 576px) {
    padding: 15px 12px;
    font-size: 16px; /* Prevent zoom on focus in iOS */
    min-height: 100px;
    border-radius: 10px;
  }
`;

const ErrorText = styled.div`
  color: #ff6b6b;
  font-size: 0.8rem;
  margin-top: 5px;
  display: flex;
  align-items: center;
  
  &::before {
    content: "⚠";
    margin-left: 5px;
  }
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fff5f5;
  border: 1px solid #ffe3e3;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  color: #e03131;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  
  svg {
    font-size: 1.2rem;
  }
  
  @media (max-width: 576px) {
    padding: 12px;
    border-radius: 10px;
    font-size: 0.85rem;
    
    svg {
      font-size: 1.1rem;
    }
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(90deg, #0076d6, #0056a6);
  color: #ffffff;
  padding: 15px 30px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  box-shadow: 0 4px 15px rgba(0, 118, 214, 0.2);
  position: relative;
  overflow: hidden;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 118, 214, 0.3);
    background: linear-gradient(90deg, #0056a6, #003f87);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &::after {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    bottom: -50%;
    left: -50%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0));
    transform: rotate(45deg) translate(0, -100px);
    transition: all 0.8s;
  }
  
  &:hover::after {
    transform: rotate(45deg) translate(0, 200px);
  }
  
  &:disabled {
    background: #b8c2cc;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
  
  @media (max-width: 576px) {
    padding: 16px 24px;
    font-size: 1.05rem;
    border-radius: 12px;
    margin-top: 15px;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  animation: fadeIn 0.5s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 576px) {
    padding: 30px 15px;
  }
`;

const SuccessIcon = styled.div`
  font-size: 5rem;
  color: #4caf50;
  margin-bottom: 20px;
  
  svg {
    filter: drop-shadow(0 5px 15px rgba(76, 175, 80, 0.3));
  }
  
  @media (max-width: 576px) {
    font-size: 4rem;
    margin-bottom: 15px;
  }
`;

const SuccessTitle = styled.h2`
  font-size: 2rem;
  color: #192a56;
  margin-bottom: 15px;
  
  @media (max-width: 576px) {
    font-size: 1.7rem;
    margin-bottom: 12px;
  }
`;

const SuccessMessage = styled.p`
  color: #596275;
  font-size: 1.1rem;
  margin-bottom: 30px;
  max-width: 500px;
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin-bottom: 25px;
  }
`;

const BackButton = styled.button`
  background: transparent;
  color: #0076d6;
  border: 2px solid #0076d6;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 118, 214, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 576px) {
    padding: 13px 24px;
    border-radius: 10px;
    width: 100%;
    max-width: 250px;
  }
`;

export default ContactUs;
